import React, { useContext } from 'react';

import InlineDialog from 'components/InlineDialog';
import usePopup from 'hooks/usePopup';
import SelectedEntityContext from 'components/PerformanceBoards/contexts/SelectedEntityContext';
import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';

import SlideOutModeHeader from '../SlideOutModeHeader';
import { BonusOverviewContext } from '../../providers/BonusOverviewProvider';
import { AdditionalPaymentsModeContext } from './AdditionalPaymentsModeProvider';
import ConfirmAdditionalPaymentDialog from './ConfirmAdditionalPaymentDialog';
import useFormatPaymentAmount from './useFormatPaymentAmount';

// The slideout header while in recognition payments mode, laid out like the
// override header: title, a status badge, then the actions.
const AdditionalPaymentsModeActions = () => {
  const { bonusOverview, exitAdditionalPaymentsMode } =
    useContext(BonusOverviewContext);
  const {
    view,
    response,
    validAmount,
    isSaving,
    isAddDisabled,
    maxAddable,
    removingPaymentId,
  } = useContext(AdditionalPaymentsModeContext);
  const formatAmount = useFormatPaymentAmount();
  const { isOpen, open, close } = usePopup();
  const { selectedEntity } = useContext(SelectedEntityContext);
  // Falls back to the selected row while the record is still loading.
  const driverName = bonusOverview
    ? bonusOverview.actualRewardOutput.driverName || bonusOverview.driver
    : selectedEntity;

  // The manage view counts the payments; the add view's budget is already
  // in the body.
  const paymentCount = response?.payments.length ?? 0;
  const manageStatus = response ? (
    <Badge badgeType={'Blue'}>
      <Typography.Body type={'Badge'}>
        {`${paymentCount} Payment${paymentCount === 1 ? '' : 's'}`}
      </Typography.Body>
    </Badge>
  ) : undefined;

  if (view === 'manage') {
    return (
      <SlideOutModeHeader
        title={'Additional Payments'}
        driverName={driverName}
        status={manageStatus}
        actions={
          <Button
            size={'Medium'}
            type={'Primary'}
            label={'Done'}
            onClick={exitAdditionalPaymentsMode}
            isDisabled={!!removingPaymentId}
            testId={'exit-additional-payments'}
          />
        }
      />
    );
  }

  return (
    <SlideOutModeHeader
      title={'Add Additional Payment'}
      driverName={driverName}
      actions={
        <>
          <Button
            size={'Medium'}
            type={'Secondary'}
            label={'Cancel'}
            onClick={exitAdditionalPaymentsMode}
            isDisabled={isSaving}
            testId={'exit-additional-payments'}
          />
          {/* Nothing can be added once the cap is reached (or the period's
              config doesn't enable payments); the body explains why. */}
          {maxAddable > 0 && (
            <InlineDialog
              content={<ConfirmAdditionalPaymentDialog close={close} />}
              isOpen={isOpen}
              onClose={close}
              hasArrow
            >
              <Button
                size={'Medium'}
                type={'Primary'}
                label={
                  validAmount !== undefined
                    ? `Add ${formatAmount(validAmount)}`
                    : 'Add Payment'
                }
                onClick={open}
                isLoading={isSaving}
                isDisabled={isAddDisabled}
                testId={'add-additional-payment'}
              />
            </InlineDialog>
          )}
        </>
      }
    />
  );
};

export default AdditionalPaymentsModeActions;
